/**
 * detectTrafficSource — detección de origen de tráfico en el frontend.
 *
 * Combina 4 señales en orden de prioridad:
 *  1. UTM params   → utm_source/medium/content/campaign (explícito, alta confianza)
 *  2. Click IDs    → fbclid (Facebook/IG ads), gclid (Google Ads), ttclid (TikTok),
 *                    igshid (Instagram), msclkid (Bing)
 *  3. User-Agent   → Instagram in-app browser, TikTok, Facebook App, WhatsApp
 *  4. Referrer     → document.referrer analizado por dominio
 *
 * Resultado: objeto `TrafficSource` con un `channel` legible para Slack y campos
 * raw para análisis.
 *
 * Uso:
 *   import { detectTrafficSource } from "@/lib/detectTrafficSource";
 *   const src = detectTrafficSource();
 *   // src.channel → "Instagram · link_in_bio"
 *   // src.source  → "instagram"
 *   // src.inAppBrowser → true
 */

export interface TrafficSource {
  /** Plataforma normalizada: "instagram" | "facebook" | "tiktok" | "google" | etc. */
  source: string;
  /** Canal de marketing: "social" | "paid_social" | "cpc" | "organic" | "referral" | "direct" | "messaging" */
  medium: string;
  /** String legible para mostrar en Slack. Ej: "Instagram · link_in_bio" */
  channel: string;
  /** true cuando el navegador es el in-app browser de Instagram, TikTok, Facebook o WhatsApp */
  inAppBrowser: boolean;
  raw: {
    utm_source?: string;
    utm_medium?: string;
    utm_content?: string;
    utm_campaign?: string;
    referrer_domain?: string;
    click_id?: string; // "fbclid" | "gclid" | "ttclid" | "igshid" | "msclkid"
  };
}

// ─── Mapas de referrer domain ─────────────────────────────────────────────────

const REFERRER_MAP: Array<{ test: (d: string) => boolean; source: string; medium: string; label: string }> = [
  { test: d => d.includes("instagram.com"),                                    source: "instagram", medium: "social",    label: "Instagram" },
  { test: d => d.includes("facebook.com") || d.includes("l.facebook.com") || d.includes("fb.me"), source: "facebook",  medium: "social",    label: "Facebook" },
  { test: d => d.includes("tiktok.com"),                                       source: "tiktok",    medium: "social",    label: "TikTok" },
  { test: d => d === "t.co" || d.includes("twitter.com") || d.includes("x.com"), source: "twitter",  medium: "social",    label: "X / Twitter" },
  { test: d => d.includes("youtube.com") || d.includes("youtu.be"),           source: "youtube",   medium: "social",    label: "YouTube" },
  { test: d => d.includes("pinterest."),                                       source: "pinterest", medium: "social",    label: "Pinterest" },
  { test: d => d.includes("linkedin.com"),                                     source: "linkedin",  medium: "social",    label: "LinkedIn" },
  { test: d => d.includes("wa.me") || d.includes("web.whatsapp.") || d.includes("api.whatsapp."), source: "whatsapp", medium: "messaging", label: "WhatsApp" },
  { test: d => d.includes("t.me") || d.includes("telegram.org"),              source: "telegram",  medium: "messaging", label: "Telegram" },
  { test: d => /google\.(com|com\.\w+|\w{2})$/.test(d),                       source: "google",    medium: "organic",   label: "Google" },
  { test: d => d.includes("bing.com"),                                         source: "bing",      medium: "organic",   label: "Bing" },
  { test: d => d.includes("duckduckgo.com"),                                   source: "duckduckgo",medium: "organic",   label: "DuckDuckGo" },
];

// ─── Mapas de utm_source ──────────────────────────────────────────────────────

const UTM_SOURCE_MAP: Array<{ test: (s: string) => boolean; source: string; label: string }> = [
  { test: s => /^ig$|^instagram/.test(s),        source: "instagram", label: "Instagram" },
  { test: s => /^fb$|^facebook/.test(s),         source: "facebook",  label: "Facebook" },
  { test: s => /^tt$|^tiktok/.test(s),           source: "tiktok",    label: "TikTok" },
  { test: s => /^yt$|^youtube/.test(s),          source: "youtube",   label: "YouTube" },
  { test: s => /^tw$|twitter|^x$/.test(s),       source: "twitter",   label: "X / Twitter" },
  { test: s => /^wa$|whatsapp/.test(s),          source: "whatsapp",  label: "WhatsApp" },
  { test: s => /^tg$|telegram/.test(s),          source: "telegram",  label: "Telegram" },
  { test: s => /^gg$|google/.test(s),            source: "google",    label: "Google" },
  { test: s => /^li$|linkedin/.test(s),          source: "linkedin",  label: "LinkedIn" },
  { test: s => /email|mail|newsletter|nl$/.test(s), source: "email",  label: "Email" },
  { test: s => /pinterest/.test(s),              source: "pinterest", label: "Pinterest" },
];

function resolveUtmSource(raw: string): { source: string; label: string } {
  const s = raw.toLowerCase().trim();
  for (const entry of UTM_SOURCE_MAP) {
    if (entry.test(s)) return entry;
  }
  return { source: s, label: raw };
}

/** Construye el `channel` legible a partir de source + medium + content */
function buildChannel(label: string, medium: string, content?: string): string {
  const parts = [label];
  // medium descriptivo solo si agrega info
  if (medium === "cpc" || medium === "paid" || medium === "paid_social") parts.push("(ad)");
  if (content) parts.push(content);
  return parts.join(" · ");
}

// ─── Función principal ────────────────────────────────────────────────────────

export function detectTrafficSource(): TrafficSource {
  if (typeof window === "undefined") {
    return { source: "unknown", medium: "unknown", channel: "Desconocido", inAppBrowser: false, raw: {} };
  }

  const params   = new URLSearchParams(window.location.search);
  const referrer = (() => { try { return document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "").toLowerCase() : ""; } catch { return ""; } })();
  const ua = navigator.userAgent;

  // Raw UTM
  const utmSource   = params.get("utm_source")?.trim()   ?? "";
  const utmMedium   = params.get("utm_medium")?.trim()   ?? "";
  const utmContent  = params.get("utm_content")?.trim()  ?? "";
  const utmCampaign = params.get("utm_campaign")?.trim() ?? "";

  // Click IDs
  const fbclid  = params.has("fbclid");
  const gclid   = params.has("gclid");
  const ttclid  = params.has("ttclid");
  const msclkid = params.has("msclkid");
  const igshid  = params.has("igshid");

  // In-app browser UA detection
  const isInstagramUA = /Instagram/i.test(ua);
  const isFacebookUA  = /FBAN|FBAV|FB_IAB|FBIOS|Messenger/i.test(ua);
  const isTikTokUA    = /TikTok|musical_ly/i.test(ua);
  const isWhatsAppUA  = /WhatsApp/i.test(ua);
  const inAppBrowser  = isInstagramUA || isFacebookUA || isTikTokUA || isWhatsAppUA;

  const raw: TrafficSource["raw"] = {
    utm_source:  utmSource  || undefined,
    utm_medium:  utmMedium  || undefined,
    utm_content: utmContent || undefined,
    utm_campaign: utmCampaign || undefined,
    referrer_domain: referrer || undefined,
  };

  // ── 1. UTM params (más alta prioridad — explícito) ─────────────────────────
  if (utmSource) {
    const { source, label } = resolveUtmSource(utmSource);
    const medium  = utmMedium || (["instagram","facebook","tiktok","twitter","youtube","linkedin","pinterest"].includes(source) ? "social" : "unknown");
    const channel = buildChannel(label, medium, utmContent || undefined);
    return { source, medium, channel, inAppBrowser, raw };
  }

  // ── 2. Click IDs ───────────────────────────────────────────────────────────
  if (igshid) {
    raw.click_id = "igshid";
    return { source: "instagram", medium: "social", channel: "Instagram", inAppBrowser, raw };
  }
  if (fbclid) {
    raw.click_id = "fbclid";
    const src = isInstagramUA ? "instagram" : "facebook";
    return { source: src, medium: "paid_social", channel: isInstagramUA ? "Instagram (ad)" : "Facebook (ad)", inAppBrowser, raw };
  }
  if (gclid) {
    raw.click_id = "gclid";
    return { source: "google", medium: "cpc", channel: "Google Ads", inAppBrowser, raw };
  }
  if (ttclid) {
    raw.click_id = "ttclid";
    return { source: "tiktok", medium: "paid_social", channel: "TikTok Ads", inAppBrowser, raw };
  }
  if (msclkid) {
    raw.click_id = "msclkid";
    return { source: "bing", medium: "cpc", channel: "Bing Ads", inAppBrowser, raw };
  }

  // ── 3. User-Agent in-app browser ──────────────────────────────────────────
  if (isInstagramUA) {
    return { source: "instagram", medium: "social", channel: "Instagram (in-app)", inAppBrowser: true, raw };
  }
  if (isTikTokUA) {
    return { source: "tiktok", medium: "social", channel: "TikTok (in-app)", inAppBrowser: true, raw };
  }
  if (isFacebookUA) {
    return { source: "facebook", medium: "social", channel: "Facebook (in-app)", inAppBrowser: true, raw };
  }
  if (isWhatsAppUA) {
    return { source: "whatsapp", medium: "messaging", channel: "WhatsApp (in-app)", inAppBrowser: true, raw };
  }

  // ── 4. Referrer ────────────────────────────────────────────────────────────
  if (referrer) {
    for (const entry of REFERRER_MAP) {
      if (entry.test(referrer)) {
        return { source: entry.source, medium: entry.medium, channel: entry.label, inAppBrowser: false, raw };
      }
    }
    // Referrer desconocido pero existe → referral
    return { source: referrer, medium: "referral", channel: `Referral (${referrer})`, inAppBrowser: false, raw };
  }

  // ── 5. Direct / unknown ────────────────────────────────────────────────────
  return { source: "direct", medium: "direct", channel: "Directo", inAppBrowser: false, raw };
}
